import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaKey as FaKeyRaw,
  FaLightbulb as FaLightbulbRaw,
  FaCopy as FaCopyRaw,
  FaEdit as FaEditRaw,
  FaPlay as FaPlayRaw,
  FaTrash as FaTrashRaw,
  FaChartLine as FaChartLineRaw,
  FaBolt as FaBoltRaw,
  FaSearch as FaSearchRaw,
} from 'react-icons/fa';
import MarketingHeader from '../components/MarketingHeader';
import SyncClearstreamButton from '../components/SyncClearstreamButton';
import {
  useCreateMarketingKeyword,
  useCreateMarketingFlow,
  useCreateMarketingSubscriptionChannel,
  useDeleteMarketingKeyword,
  useMarketingFlowStepCounts,
  useMarketingKeywords,
  useMarketingSubscribers,
  useMarketingSubscriptionChannels,
  useUpdateMarketingKeyword,
} from '../hooks/useMarketing';
import type { MarketingKeyword } from '../hooks/useMarketing';
import {
  MarketingPageShell,
  TableCard,
  TableWrap,
  Table,
  TableFooter,
  PrimaryButton,
  PaginationButton,
} from '../styles/marketingSharedStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaKey: any = FaKeyRaw;
const FaLightbulb: any = FaLightbulbRaw;
const FaCopy: any = FaCopyRaw;
const FaEdit: any = FaEditRaw;
const FaPlay: any = FaPlayRaw;
const FaTrash: any = FaTrashRaw;
const FaChartLine: any = FaChartLineRaw;
const FaBolt: any = FaBoltRaw;
const FaSearch: any = FaSearchRaw;

const PageWrap = styled.div`
  min-height: 100%;
  background: var(--background);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 1rem;
  overflow-x: clip;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--primary);
  }
`;

const Content = styled(MarketingPageShell).attrs({ as: 'main' })`
  flex: 0 0 auto;
  margin-top: 1rem;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  @media (max-width: 760px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.9rem;
  }
`;

const PageHeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.25rem 0;
  font-size: clamp(1.35rem, 2.3vw, 1.85rem);
  font-weight: 700;
  color: var(--onSurface);
`;

const PageSub = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--onSurfaceVariant);
`;

const PageHeaderRight = styled.div`
  flex-shrink: 0;
  @media (max-width: 760px) {
    width: 100%;
    > * {
      width: 100%;
      justify-content: center;
    }
  }
`;

const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SearchWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1;
  min-width: 220px;
  max-width: 420px;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.65rem 1rem;
  background: var(--surface);
  color: var(--onSurfaceVariant);
  @media (max-width: 760px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--onSurface);
  font-size: 0.94rem;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.div`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%), var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.16);
  min-width: 0;
`;

const KpiIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--surfaceVariant);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const KpiContent = styled.div``;

const KpiLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
`;

const KpiValue = styled.div`
  font-size: clamp(1.2rem, 2vw, 1.65rem);
  font-weight: 700;
  color: var(--onSurface);
`;


const KeywordPill = styled.span<{ $disabled?: boolean }>`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  background: ${({ $disabled }) => ($disabled ? 'var(--outlineVariant)' : 'var(--primary)')};
  color: ${({ $disabled }) => ($disabled ? 'var(--onSurfaceVariant)' : 'var(--onPrimary)')};
`;

const KeywordLink = styled.button<{ $disabled?: boolean }>`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 8px;
  }
  ${KeywordPill} {
   
    text-underline-offset: 2px;
  }
`;

const SubscriberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const SubscriberName = styled.span`
  font-size: 0.82rem;
  color: var(--onSurface);
  line-height: 1.25;
`;

const SubscriberHint = styled.span`
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const StatusDot = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? 'var(--success)' : 'var(--onSurfaceVariant)')};
  margin-right: 0.35rem;
`;

const StatusText = styled.span`
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
`;

const ActionBtns = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ProTipBox = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(130deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.015) 100%), var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 16px;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.14);
  @media (max-width: 640px) {
    padding: 1rem;
  }
`;

const ProTipIcon = styled.div`
  color: var(--primary);
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const ProTipContent = styled.div`
  flex: 1;
`;

const ProTipTitle = styled.div`
  font-weight: 600;
  color: var(--onSurface);
  margin-bottom: 0.35rem;
  font-size: 0.95rem;
`;

const ProTipText = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
  line-height: 1.5;
`;

const ProTipLink = styled.a`
  font-size: 0.9rem;
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const PAGE_SIZE = 4;
const EMPTY_KEYWORDS: MarketingKeyword[] = [];
const EMPTY_CHANNELS: Array<{ id: string; name: string }> = [];

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.5rem;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 20px 35px rgba(0, 0, 0, 0.24);
`;

const ModalTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--onSurface);
`;

const ModalField = styled.div`
  margin-bottom: 1rem;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.35rem;
`;

const ModalInput = styled.input`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  background: var(--surface);
  color: var(--onSurface);
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ModalSelect = styled.select`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  background: var(--surface);
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
`;

const ModalCancelBtn = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const ModalSubmitBtn = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: var(--primaryDark);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function KeywordManagementPage() {
  const navigate = useNavigate();
  const keywordsQuery = useMarketingKeywords();
  const subscribersQuery = useMarketingSubscribers();
  const channelsQuery = useMarketingSubscriptionChannels();
  const createKeyword = useCreateMarketingKeyword();
  const createFlow = useCreateMarketingFlow();
  const createChannel = useCreateMarketingSubscriptionChannel();
  const updateKeyword = useUpdateMarketingKeyword();
  const deleteKeyword = useDeleteMarketingKeyword();

  const [currentPage, setCurrentPage] = useState(1);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newChannelId, setNewChannelId] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [editingKeyword, setEditingKeyword] = useState<MarketingKeyword | null>(null);

  const keywords = useMemo(() => keywordsQuery.data ?? EMPTY_KEYWORDS, [keywordsQuery.data]);
  const flowIds = useMemo(
    () =>
      Array.from(new Set(keywords.map((kw) => kw.flow_id).filter((flowId): flowId is string => Boolean(flowId)))).sort(),
    [keywords]
  );
  const flowStepCountsQuery = useMarketingFlowStepCounts(flowIds);
  const flowStepCounts = flowStepCountsQuery.data ?? {};
  const channels = channelsQuery.data ?? EMPTY_CHANNELS;
  const channelNameById = useMemo(() => {
    const map = new Map<string, string>();
    channels.forEach((channel) => map.set(channel.id, channel.name));
    return map;
  }, [channels]);
  const subscriberNamesByChannel = useMemo(() => {
    const map = new Map<string, string[]>();
    const subscribers = subscribersQuery.data ?? [];
    for (const subscriber of subscribers) {
      const displayName =
        `${subscriber.first_name ?? ''} ${subscriber.last_name ?? ''}`.trim() || subscriber.phone_e164;
      for (const membership of subscriber.channel_memberships ?? []) {
        if (membership.unsubscribed_at) continue;
        const channelId = membership.channel_id;
        const existing = map.get(channelId) ?? [];
        existing.push(displayName);
        map.set(channelId, existing);
      }
    }
    map.forEach((names, channelId) => {
      const deduped = Array.from(new Set(names.map((item) => item.trim()).filter(Boolean)));
      map.set(channelId, deduped);
    });
    return map;
  }, [subscribersQuery.data]);

  const filtered = useMemo(() => {
    const term = keywordSearch.trim().toLowerCase();
    if (!term) return keywords;
    return keywords.filter((kw) => kw.keyword.toLowerCase().includes(term));
  }, [keywords, keywordSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [keywordSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageIndex = Math.min(currentPage, totalPages);
  const paginated = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, pageIndex]);

  const totalKeywords = keywords.length;
  const totalTriggers = keywords.reduce((sum, kw) => sum + (kw.trigger_count ?? 0), 0);
  const activeCount = keywords.filter((kw) => kw.is_active).length;
  const avgResponseRate = totalKeywords > 0 ? `${Math.round((activeCount / totalKeywords) * 100)}%` : '0%';

  const handleToggleActive = async (kw: MarketingKeyword) => {
    try {
      await updateKeyword.mutateAsync({ id: kw.id, changes: { is_active: !kw.is_active } });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to update keyword.');
    }
  };

  const handleDelete = async (kw: MarketingKeyword) => {
    if (!window.confirm(`Delete keyword "${kw.keyword}"?`)) return;
    try {
      await deleteKeyword.mutateAsync(kw.id);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to delete keyword.');
    }
  };

  const handleOpenFunnel = async (kw: MarketingKeyword) => {
    try {
      if (kw.flow_id) {
        navigate(`/marketing/sms-flow/${kw.flow_id}`);
        return;
      }
      const createdFlow = await createFlow.mutateAsync({
        name: `${kw.keyword} Funnel`,
        description: `Auto-created funnel for keyword ${kw.keyword}`,
        status: 'draft',
      });
      await updateKeyword.mutateAsync({
        id: kw.id,
        changes: { flow_id: createdFlow.id },
      });
      navigate(`/marketing/sms-flow/${createdFlow.id}`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to open funnel.');
    }
  };

  const handleDuplicateKeyword = async (kw: MarketingKeyword) => {
    try {
      const existing = new Set((keywordsQuery.data ?? []).map((item) => item.keyword.trim().toUpperCase()));
      const base = `${kw.keyword.trim().toUpperCase()}_COPY`;
      let candidate = base;
      let index = 2;
      while (existing.has(candidate)) {
        candidate = `${base}${index}`;
        index += 1;
      }
      await createKeyword.mutateAsync({
        keyword: candidate,
        subscriptionChannelId: kw.subscription_channel_id,
        flowId: null,
        isActive: kw.is_active,
      });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to duplicate keyword.');
    }
  };

  const handleAddKeyword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyword.trim()) {
      window.alert('Please enter a keyword.');
      return;
    }
    try {
      let subscriptionChannelId = newChannelId || null;
      if (!subscriptionChannelId && newChannelName.trim()) {
        const createdChannel = await createChannel.mutateAsync({
          name: newChannelName.trim(),
          description: `Auto-created for keyword ${newKeyword.trim().toUpperCase()}`,
        });
        subscriptionChannelId = createdChannel.id;
      }
      await createKeyword.mutateAsync({
        keyword: newKeyword.trim(),
        subscriptionChannelId,
      });
      setAddModalOpen(false);
      setNewKeyword('');
      setNewChannelId('');
      setNewChannelName('');
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to add keyword.');
    }
  };

  const handleSaveEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingKeyword) return;
    try {
      let subscriptionChannelId = editingKeyword.subscription_channel_id;
      if (!subscriptionChannelId && newChannelName.trim()) {
        const createdChannel = await createChannel.mutateAsync({
          name: newChannelName.trim(),
          description: `Auto-created for keyword ${editingKeyword.keyword}`,
        });
        subscriptionChannelId = createdChannel.id;
      }
      await updateKeyword.mutateAsync({
        id: editingKeyword.id,
        changes: {
          keyword: editingKeyword.keyword,
          subscription_channel_id: subscriptionChannelId ?? null,
          is_active: editingKeyword.is_active,
        },
      });
      setEditingKeyword(null);
      setNewChannelName('');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Failed to update keyword.');
    }
  };

  return (
    <PageWrap>
      <MarketingHeader activeTab="keywords" rightSlot={<SyncClearstreamButton />} />

      <Content>
        <PageHeaderRow>
          <PageHeaderLeft>
            <PageTitle>Keyword Management</PageTitle>
            <PageSub>
              Manage each keyword funnel and see who subscribed through each keyword.
            </PageSub>
          </PageHeaderLeft>
          <PageHeaderRight>
            <PrimaryButton type="button" onClick={() => setAddModalOpen(true)}>
              + Add New Keyword
            </PrimaryButton>
          </PageHeaderRight>
        </PageHeaderRow>

        <KpiGrid>
          <KpiCard>
            <KpiIcon>
              <FaKey size={20} />
            </KpiIcon>
            <KpiContent>
              <KpiLabel>Total Keywords</KpiLabel>
              <KpiValue>{totalKeywords}</KpiValue>
            </KpiContent>
          </KpiCard>
          <KpiCard>
            <KpiIcon>
              <FaBolt size={20} />
            </KpiIcon>
            <KpiContent>
              <KpiLabel>Total Triggers</KpiLabel>
              <KpiValue>{totalTriggers.toLocaleString()}</KpiValue>
            </KpiContent>
          </KpiCard>
          <KpiCard>
            <KpiIcon>
              <FaChartLine size={20} />
            </KpiIcon>
            <KpiContent>
              <KpiLabel>Avg. Response Rate</KpiLabel>
              <KpiValue>{avgResponseRate}</KpiValue>
            </KpiContent>
          </KpiCard>
        </KpiGrid>

        <TableToolbar>
          <SearchWrap htmlFor="keyword-table-search">
            <FaSearch size={14} aria-hidden />
            <SearchInput
              id="keyword-table-search"
              type="search"
              value={keywordSearch}
              onChange={(e) => setKeywordSearch(e.target.value)}
              placeholder="Search keywords..."
              autoComplete="off"
            />
          </SearchWrap>
        </TableToolbar>

        <TableCard>
          <TableWrap>
            <Table $mobileCards>
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th>Subscription Channel</th>
                  <th>Subscribers</th>
                  <th style={{ textAlign: 'right' }}>Flow Steps</th>
                  <th style={{ textAlign: 'right' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keywordsQuery.isLoading && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                      Loading keywords...
                    </td>
                  </tr>
                )}
                {keywordsQuery.error && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--error)' }}>
                      Failed to load keywords.
                    </td>
                  </tr>
                )}
                {!keywordsQuery.isLoading && !keywordsQuery.error && paginated.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--onSurfaceVariant)' }}>
                      {keywords.length === 0
                        ? 'No keywords yet. Add one to get started.'
                        : 'No keywords match your search.'}
                    </td>
                  </tr>
                )}
                {paginated.map((kw) => {
                  const subscriberNames = kw.subscription_channel_id
                    ? subscriberNamesByChannel.get(kw.subscription_channel_id) ?? []
                    : [];
                  const previewNames = subscriberNames.slice(0, 3);
                  const remaining = Math.max(0, subscriberNames.length - previewNames.length);
                  return (
                    <tr key={kw.id}>
                      <td data-label="Keyword">
                        <KeywordLink
                          type="button"
                          $disabled={!kw.is_active}
                          onClick={() => handleOpenFunnel(kw)}
                          title={kw.flow_id ? 'Open Funnel' : 'Create + Open Funnel'}
                          disabled={createFlow.isPending || updateKeyword.isPending}
                        >
                          <KeywordPill $disabled={!kw.is_active}>{kw.keyword}</KeywordPill>
                        </KeywordLink>
                      </td>
                      <td data-label="Subscription Channel">{kw.subscription_channel_id ? channelNameById.get(kw.subscription_channel_id) ?? '—' : '—'}</td>
                      <td data-label="Subscribers">
                        <SubscriberList>
                          {subscribersQuery.isLoading ? (
                            <SubscriberHint>Loading subscribers...</SubscriberHint>
                          ) : previewNames.length > 0 ? (
                            <>
                              {previewNames.map((name) => (
                                <SubscriberName key={`${kw.id}-${name}`}>{name}</SubscriberName>
                              ))}
                              {remaining > 0 ? <SubscriberHint>+{remaining} more</SubscriberHint> : null}
                            </>
                          ) : (
                            <SubscriberHint>No active subscribers</SubscriberHint>
                          )}
                        </SubscriberList>
                      </td>
                      <td data-label="Flow Steps" style={{ textAlign: 'right' }}>
                        {kw.flow_id ? (flowStepCounts[kw.flow_id] ?? 0).toLocaleString() : '0'}
                      </td>
                      <td data-label="Status" style={{ textAlign: 'right' }}>
                        <StatusDot $active={kw.is_active} />
                        <StatusText>{kw.is_active ? 'Active' : 'Disabled'}</StatusText>
                      </td>
                      <td data-label="Actions" style={{ textAlign: 'right' }}>
                        <ActionBtns>
                          <IconBtn
                            type="button"
                            aria-label="Edit keyword"
                            title="Edit keyword"
                            onClick={() => {
                              setEditingKeyword(kw);
                              setNewChannelName('');
                            }}
                          >
                            <FaEdit size={14} />
                          </IconBtn>
                          <IconBtn
                            type="button"
                            aria-label="Open funnel"
                            title={kw.flow_id ? 'Open Funnel' : 'Create + Open Funnel'}
                            onClick={() => handleOpenFunnel(kw)}
                            disabled={createFlow.isPending || updateKeyword.isPending}
                          >
                            <FaBolt size={14} />
                          </IconBtn>
                          <IconBtn
                            type="button"
                            aria-label="Duplicate"
                            title="Duplicate"
                            onClick={() => handleDuplicateKeyword(kw)}
                            disabled={createKeyword.isPending}
                          >
                            <FaCopy size={14} />
                          </IconBtn>
                          {kw.is_active ? (
                            <IconBtn
                              type="button"
                              aria-label="Delete"
                              title="Delete"
                              onClick={() => handleDelete(kw)}
                            >
                              <FaTrash size={14} />
                            </IconBtn>
                          ) : (
                            <IconBtn
                              type="button"
                              aria-label="Enable"
                              title="Enable"
                              onClick={() => handleToggleActive(kw)}
                            >
                              <FaPlay size={14} />
                            </IconBtn>
                          )}
                        </ActionBtns>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
          {filtered.length > 0 && (
            <TableFooter>
              <span>
                Showing {(pageIndex - 1) * PAGE_SIZE + 1} to{' '}
                {Math.min(pageIndex * PAGE_SIZE, filtered.length)} of {filtered.length} keywords
              </span>
              <Pagination>
                <PaginationButton
                  type="button"
                  aria-label="Previous page"
                  disabled={pageIndex <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  ←
                </PaginationButton>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <PaginationButton
                    key={p}
                    type="button"
                    $active={p === pageIndex}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </PaginationButton>
                ))}
                <PaginationButton
                  type="button"
                  aria-label="Next page"
                  disabled={pageIndex >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  →
                </PaginationButton>
              </Pagination>
            </TableFooter>
          )}
        </TableCard>

        <ProTipBox>
          <ProTipIcon>
            <FaLightbulb size={24} />
          </ProTipIcon>
          <ProTipContent>
            <ProTipTitle>Pro Tip: Using Global Keywords</ProTipTitle>
            <ProTipText>
              Keywords like STOP, CANCEL, and QUIT are handled automatically by the carrier system to ensure
              compliance. You don&apos;t need to create custom responses for these.
            </ProTipText>
            <ProTipLink href="#" target="_blank" rel="noopener noreferrer">
              Learn more about compliance →
            </ProTipLink>
          </ProTipContent>
        </ProTipBox>
      </Content>

      {addModalOpen && (
        <ModalOverlay onClick={() => setAddModalOpen(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Add New Keyword</ModalTitle>
            <form onSubmit={handleAddKeyword}>
              <ModalField>
                <ModalLabel>Keyword</ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="e.g. JOIN, HELP"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  autoFocus
                />
              </ModalField>
              <ModalField>
                <ModalLabel>Subscription channel</ModalLabel>
                <ModalSelect value={newChannelId} onChange={(e) => setNewChannelId(e.target.value)}>
                  <option value="">Select existing channel (optional)</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </ModalSelect>
              </ModalField>
              <ModalField>
                <ModalLabel>Or create a new channel</ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="e.g. KINGMEN, YOUTH, FIRST-TIME GUESTS"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  disabled={!!newChannelId}
                />
              </ModalField>
              <ModalActions>
                <ModalCancelBtn type="button" onClick={() => setAddModalOpen(false)}>
                  Cancel
                </ModalCancelBtn>
                <ModalSubmitBtn type="submit" disabled={createKeyword.isPending}>
                  {createKeyword.isPending ? 'Adding...' : 'Add Keyword'}
                </ModalSubmitBtn>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
      {editingKeyword && (
        <ModalOverlay onClick={() => setEditingKeyword(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Edit Keyword</ModalTitle>
            <form onSubmit={handleSaveEdit}>
              <ModalField>
                <ModalLabel>Keyword</ModalLabel>
                <ModalInput
                  type="text"
                  value={editingKeyword.keyword}
                  onChange={(e) =>
                    setEditingKeyword((prev) => (prev ? { ...prev, keyword: e.target.value.toUpperCase() } : prev))
                  }
                />
              </ModalField>
              <ModalField>
                <ModalLabel>Subscription channel</ModalLabel>
                <ModalSelect
                  value={editingKeyword.subscription_channel_id ?? ''}
                  onChange={(e) =>
                    setEditingKeyword((prev) =>
                      prev ? { ...prev, subscription_channel_id: e.target.value || null } : prev
                    )
                  }
                >
                  <option value="">None</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </ModalSelect>
              </ModalField>
              <ModalField>
                <ModalLabel>Or create a new channel</ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="New channel name"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  disabled={!!editingKeyword.subscription_channel_id}
                />
              </ModalField>
              <ModalActions>
                <ModalCancelBtn type="button" onClick={() => setEditingKeyword(null)}>
                  Cancel
                </ModalCancelBtn>
                <ModalSubmitBtn type="submit" disabled={updateKeyword.isPending || createChannel.isPending}>
                  {updateKeyword.isPending ? 'Saving...' : 'Save Changes'}
                </ModalSubmitBtn>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageWrap>
  );
}
